import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import styles from "./styles.module.css";
import {
  updateCourse,
  updateLessonPlan,
  updateHomework,
} from "../../../../../store/actions/course";
import { languages, CURRENCIES } from "../../../../../utils/constants";

const ViewCourse = ({ courseData, modalType, setModal, activeTab, setApiCalled }) => {
  const dispatch = useDispatch();
  const courseImgInput = useRef();
  const [imgUrl, setImgUrl] = useState("");
  const [formValues, setFormValues] = useState({
    title: "",
    courseImage: "",
    course: "", // Only for Courses
    language: "", // Only for Courses
    program: "", // Only for Courses
    price: "", // Only for Courses
    price1: "", // Only for Courses
    price2: "", // Only for Courses
    description: "",
    level: "", // Only for Lessons
    currency: "INR",
  });
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    if (!courseData) return;

    const getData = (field) => {
      if (typeof field === "object" && field !== null && "data" in field) return field.data;
      return field || "";
    };

    setFormValues({
      title: getData(courseData.title),
      courseImage: getData(courseData.courseImage) || getData(courseData.coverImage),
      course: getData(courseData.course),
      language: getData(courseData.language),
      program: getData(courseData.program),
      price: getData(courseData.price),
      price1: getData(courseData.price1),
      price2: getData(courseData.price2),
      description: getData(courseData.description),
      level: getData(courseData.level),
      currency: getData(courseData.currency) || "INR",
    });

    const currentImg = getData(courseData.courseImage) || getData(courseData.coverImage);
    setImgUrl(currentImg || "https://via.placeholder.com/150");

    if (courseData.language && courseData.course) {
      const lang = getData(courseData.language);
      const crs = getData(courseData.course);
      setPrograms(languages[lang]?.[crs] || []);
    }
  }, [courseData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    if (name === "language" || name === "course") {
      const nextLang = name === "language" ? value : formValues.language;
      const nextCrs = name === "course" ? value : formValues.course;
      setPrograms(languages[nextLang]?.[nextCrs] || []);
      setFormValues((prev) => ({ ...prev, program: "" }));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files.length) {
      setFormValues((prev) => ({ ...prev, courseImage: e.target.files[0] }));
      setImgUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const imageClick = () => {
    if (modalType === "Edit") courseImgInput.current.click();
  };

  const getSingular = (name) => {
    if (name === "Homework") return "Homework";
    return name.slice(0, -1);
  };

  const handleSubmit = async () => {
    if (!formValues.title || !formValues.description) {
      return toast.warn("Please add a title and description");
    }

    try {
      const form = new FormData();
      form.append("id", courseData._id || courseData.id);
      form.append("title", formValues.title);
      form.append("description", formValues.description);

      // Handle image - only append if it's a new file
      if (formValues.courseImage instanceof File) {
        form.append("courseImage", formValues.courseImage, formValues.courseImage.name);
      }

      document.getElementById("loader").style.display = "flex";
      let result;

      if (activeTab === "Courses") {
        form.append("language", formValues.language);
        form.append("course", formValues.course);
        form.append("program", formValues.program);
        form.append("price", formValues.price);
        form.append("price1", formValues.price1 || 0);
        form.append("price2", formValues.price2 || 0);
        form.append("currency", formValues.currency || "INR");
        result = await dispatch(updateCourse(form));
      } else if (activeTab === "Lesson Plans") {
        form.append("level", formValues.level);
        result = await dispatch(updateLessonPlan(form));
      } else if (activeTab === "Homework") {
        result = await dispatch(updateHomework(form));
      }

      document.getElementById("loader").style.display = "none";

      if (result?.status === "success" || result?.status === 1 || (typeof result === "string" && result.includes("Successfully"))) {
        toast.success(`${getSingular(activeTab)} Updated Successfully`);
        setApiCalled((prev) => !prev);
        setModal(false);
      } else {
        toast.error(result?.message || result?.msg || "Failed to update");
      }
    } catch (err) {
      console.error(err);
      document.getElementById("loader").style.display = "none";
      toast.error("An error occurred");
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <h3 className={styles.heading}>
          {modalType} {getSingular(activeTab)}
          <i className={`${styles.closeBtn} fas fa-close`} onClick={() => setModal(false)}></i>
        </h3>

        <form>
          <div className={styles.course}>
            <div style={{ position: 'relative' }}>
              <img src={imgUrl} alt="coverImg" className={styles.coverImg} style={{ cursor: modalType === "Edit" ? 'pointer' : 'default' }} onClick={imageClick} />
              {modalType === "Edit" && <div style={{ position: 'absolute', bottom: '10px', right: '40px', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>Click to change</div>}
            </div>
            <input type="file" name="courseImage" ref={courseImgInput} style={{ display: "none" }} onChange={handleFileInput} />

            <div className={styles.courseDetails}>
              {activeTab === "Courses" && (
                <>
                  <div>
                    <h4>Language:</h4>
                    <select name="language" value={formValues.language} onChange={handleChange} disabled={modalType === "View"}>
                      {Object.keys(languages).map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <h4>Course:</h4>
                    <select name="course" value={formValues.course} onChange={handleChange} disabled={modalType === "View"}>
                      {["Academics", "Spoken Languages", "Test Preparation"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <h4>Program:</h4>
                    <select name="program" value={formValues.program} onChange={handleChange} disabled={modalType === "View"}>
                      <option value="">Select Program</option>
                      {programs.map((prog) => <option key={prog} value={prog}>{prog}</option>)}
                    </select>
                  </div>
                  <div>
                    <h4>Currency:</h4>
                    <select name="currency" value={formValues.currency} onChange={handleChange} disabled={modalType === "View"}>
                      {CURRENCIES.map((curr) => (
                        <option key={curr.code} value={curr.code}>{curr.code} ({curr.symbol})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <h4>Price ({CURRENCIES.find(c => c.code === formValues.currency)?.symbol || "$"}):</h4>
                    <input type="number" name="price" value={formValues.price} onChange={handleChange} disabled={modalType === "View"} />
                  </div>
                  <div>
                    <h4>Price1 ({CURRENCIES.find(c => c.code === formValues.currency)?.symbol || "$"}):</h4>
                    <input type="number" name="price1" value={formValues.price1} onChange={handleChange} disabled={modalType === "View"} />
                  </div>
                  <div>
                    <h4>Price2 ({CURRENCIES.find(c => c.code === formValues.currency)?.symbol || "$"}):</h4>
                    <input type="number" name="price2" value={formValues.price2} onChange={handleChange} disabled={modalType === "View"} />
                  </div>
                </>
              )}

              {activeTab === "Lesson Plans" && (
                <div>
                  <h4>Lesson Level:</h4>
                  <input type="text" name="level" value={formValues.level} onChange={handleChange} disabled={modalType === "View"} placeholder="e.g. Intermediate" />
                </div>
              )}
            </div>
          </div>

          <div style={{ textAlign: 'left', marginTop: '1.5em' }}>
            <h4 style={{ color: '#349ed2', marginBottom: '0.5em' }}>Title:</h4>
            <input type="text" name="title" value={formValues.title} onChange={handleChange} disabled={modalType === "View"} style={{ width: '100%', fontSize: '1.1em' }} placeholder="Title" />
          </div>

          <div style={{ textAlign: 'left', marginTop: '1em' }}>
            <h4 style={{ color: '#349ed2', marginBottom: '0.5em' }}>Description:</h4>
            <textarea name="description" value={formValues.description} onChange={handleChange} disabled={modalType === "View"} style={{ width: '100%', minHeight: '120px' }} placeholder="Description"></textarea>
          </div>
        </form>

        {modalType === "Edit" && (
          <div className={styles.submitButtonContainer}>
            <button onClick={handleSubmit}>Save Changes</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCourse;
